import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import './HostedCheckout.css';
import { capturePostHogEvent } from '../../utils/analytics';
import { createCheckoutAttemptId } from '../../utils/checkoutAttemptId';
import {
  createHostedCheckoutSession,
  HOSTED_CHECKOUT_FEATURE_FLAG,
  HostedCheckoutError,
  type HostedCheckoutItem,
} from '../../utils/hostedCheckout';

type Props = {
  items: HostedCheckoutItem[];
  promoCode?: string | null;
  returnContext: 'subscriptions' | 'signup_survey' | 'ai_agent';
  sourceRoute: string;
  flowContext?: string | null;
  surface: string;
  assignment: {
    assignmentId: string;
    subjectId: string;
    exposedAt: string;
  };
  getApiToken?: (() => Promise<string | null>) | null;
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';

export default function HostedCheckoutLauncher({
  items,
  promoCode,
  returnContext,
  sourceRoute,
  flowContext,
  surface,
  assignment,
  getApiToken,
}: Props) {
  const attemptIdRef = useRef<string | null>(null);
  if (!attemptIdRef.current) attemptIdRef.current = createCheckoutAttemptId('hosted_checkout');
  const requestRef = useRef<{
    attemptId: string;
    purchaseKey: string;
    promise: ReturnType<typeof createHostedCheckoutSession>;
  } | null>(null);
  const [attemptNumber, setAttemptNumber] = useState(0);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState<string>();
  const [omitPromoCode, setOmitPromoCode] = useState(false);
  const purchaseKey = JSON.stringify({
    items,
    promoCode: omitPromoCode ? null : promoCode,
    returnContext,
    sourceRoute,
    flowContext,
    surface,
    assignment,
  });

  useEffect(() => {
    let active = true;
    if (requestRef.current && requestRef.current.purchaseKey !== purchaseKey) {
      attemptIdRef.current = createCheckoutAttemptId('hosted_checkout');
      setError('');
      setErrorCode(undefined);
    }
    const checkoutAttemptId = attemptIdRef.current;
    if (!checkoutAttemptId) return;
    const eventProps = {
      checkout_attempt_id: checkoutAttemptId,
      checkout_experience_assignment_id: assignment.assignmentId,
      checkout_experience_subject_id: assignment.subjectId,
      checkout_experience_surface: surface,
      checkout_experience_variant: 'stripe_hosted',
      checkout_experience_feature_flag: HOSTED_CHECKOUT_FEATURE_FLAG,
      source: sourceRoute,
    };
    if (requestRef.current?.attemptId !== checkoutAttemptId) {
      capturePostHogEvent('checkout_experience_session_requested', eventProps);
      requestRef.current = {
        attemptId: checkoutAttemptId,
        purchaseKey,
        promise: (async () => {
          const token = getApiToken ? await getApiToken() : null;
          return createHostedCheckoutSession({
            apiUrl: API_URL,
            token,
            checkoutAttemptId,
            items,
            promoCode: omitPromoCode ? null : promoCode,
            returnContext,
            attribution: {
              sourceRoute,
              flowContext,
              experimentKey: HOSTED_CHECKOUT_FEATURE_FLAG,
              variant: 'stripe_hosted',
              assignmentId: assignment.assignmentId,
              subjectId: assignment.subjectId,
              surface,
              exposedAt: assignment.exposedAt,
            },
          });
        })(),
      };
    }
    void requestRef.current.promise
      .then((result) => {
        if (!active) return;
        capturePostHogEvent('checkout_experience_session_created', {
          ...eventProps,
          stripe_checkout_session_id: result.sessionId,
        });
        capturePostHogEvent('checkout_experience_redirected', eventProps);
        window.location.assign(result.checkoutUrl);
      })
      .catch((launchError) => {
        if (!active) return;
        const message =
          launchError instanceof Error
            ? launchError.message
            : 'Secure checkout could not be opened. Please try again.';
        setError(message);
        setErrorCode(launchError instanceof HostedCheckoutError ? launchError.code : undefined);
        capturePostHogEvent('checkout_experience_launch_failed', {
          ...eventProps,
          error_message: message,
        });
      });
    return () => {
      active = false;
    };
  }, [
    assignment,
    attemptNumber,
    flowContext,
    getApiToken,
    items,
    omitPromoCode,
    promoCode,
    purchaseKey,
    returnContext,
    sourceRoute,
    surface,
  ]);

  const retry = (removePromo = false) => {
    attemptIdRef.current = createCheckoutAttemptId('hosted_checkout');
    if (removePromo) setOmitPromoCode(true);
    setError('');
    setErrorCode(undefined);
    setAttemptNumber((value) => value + 1);
  };

  return (
    <main className="checkout-page" aria-live="polite">
      <div className="checkout-loading hosted-checkout-status">
        {error ? (
          <>
            <h1>Secure checkout could not open</h1>
            <p role="alert">{error}</p>
            <button type="button" className="btn btn-primary" onClick={() => retry()}>
              Try again
            </button>
            {errorCode === 'INVALID_PROMOTION' && promoCode && !omitPromoCode && (
              <button type="button" className="btn btn-secondary" onClick={() => retry(true)}>
                Continue without promo code
              </button>
            )}
          </>
        ) : (
          <>
            <Spinner animation="border" />
            <span>Opening secure Stripe checkout…</span>
          </>
        )}
      </div>
    </main>
  );
}
